/**
 * Program Filters Component - Filter sidebar for programs
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@voilajsx/uikit/button';
import { Label } from '@voilajsx/uikit/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@voilajsx/uikit/select';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import type { ProgramFilterOptions, ProgramFilters } from '../types';

interface ProgramFiltersProps {
  filters: ProgramFilters;
  filterOptions: ProgramFilterOptions;
  onFilterChange: (filters: ProgramFilters) => void;
  onReset: () => void;
}

export const ProgramFilters: React.FC<ProgramFiltersProps> = ({
  filters,
  filterOptions,
  onFilterChange,
  onReset
}) => {
  const [localFilters, setLocalFilters] = useState<ProgramFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleApply = () => {
    onFilterChange(localFilters);
  };

  const hasActiveFilters =
    localFilters.field ||
    localFilters.degreeType ||
    localFilters.country ||
    typeof localFilters.isOnline === 'boolean';

  return (
    <div className="bg-card rounded-3xl p-7 shadow-sm border border-border">
      {/* Header */}
      <div className="flex items-center justify-between mb-7">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
            <SlidersHorizontal className="h-5 w-5 text-success" />
          </div>
          <h2 className="text-xl font-bold text-foreground m-0">
            Filters
          </h2>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="text-muted-foreground px-3 py-1.5 text-sm font-semibold"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-6">
        {/* Academic Field */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Academic Field
          </Label>
          <Select
            value={localFilters.field || 'all'}
            onValueChange={(value) =>
              setLocalFilters({ ...localFilters, field: value === 'all' ? undefined : value })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="All fields" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="all" className="text-[15px]">All Fields</SelectItem>
              {filterOptions.fields.map((field) => (
                <SelectItem key={field} value={field} className="text-[15px]">
                  {field}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Degree Type */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Degree Type
          </Label>
          <Select
            value={localFilters.degreeType || 'all'}
            onValueChange={(value) =>
              setLocalFilters({ ...localFilters, degreeType: value === 'all' ? undefined : value })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="All degrees" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="all" className="text-[15px]">All Degrees</SelectItem>
              {filterOptions.degreeTypes.map((type) => (
                <SelectItem key={type} value={type} className="text-[15px]">
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Country */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Country
          </Label>
          <Select
            value={localFilters.country || 'all'}
            onValueChange={(value) =>
              setLocalFilters({ ...localFilters, country: value === 'all' ? undefined : value })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="All countries" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg max-h-80">
              <SelectItem value="all" className="text-[15px]">All Countries</SelectItem>
              {filterOptions.countries.map((country) => (
                <SelectItem key={country.id} value={country.name} className="text-[15px]">
                  {country.flag_emoji} {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Study Mode */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Study Mode
          </Label>
          <Select
            value={
              typeof localFilters.isOnline === 'boolean'
                ? localFilters.isOnline
                  ? 'online'
                  : 'campus'
                : 'all'
            }
            onValueChange={(value) => {
              if (value === 'all') {
                setLocalFilters({ ...localFilters, isOnline: undefined });
              } else if (value === 'online') {
                setLocalFilters({ ...localFilters, isOnline: true });
              } else {
                setLocalFilters({ ...localFilters, isOnline: false });
              }
            }}
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="All modes" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="all" className="text-[15px]">All Modes</SelectItem>
              <SelectItem value="campus" className="text-[15px]">On Campus</SelectItem>
              <SelectItem value="online" className="text-[15px]">Online Available</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Sort By */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Sort By
          </Label>
          <Select
            value={localFilters.sort || 'university_name'}
            onValueChange={(value) =>
              setLocalFilters({ ...localFilters, sort: value })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="university_name" className="text-[15px]">University Name</SelectItem>
              <SelectItem value="name" className="text-[15px]">Program Name (A-Z)</SelectItem>
              <SelectItem value="duration" className="text-[15px]">Duration</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Apply Button */}
      <Button
        onClick={handleApply}
        className="w-full bg-success text-primary-foreground hover:bg-success/90 px-3.5 py-3.5 rounded-xl font-semibold text-[15px] mt-7"
      >
        Apply Filters
      </Button>
    </div>
  );
};
